import { Element } from "hast";
import React, { useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { CheckCircleIcon, CopyToClipboard, SectionExpandable, XIcon } from "@prequel/shared-components";

import DownloadMenu from "./DownloadMenu";

import { useLightbox } from "@/providers/LightboxProvider";

type DocumentationSidebarProps = {
  markdown: string;
  title: string;
  onClose: () => void;
};
const DocumentationSidebar = ({ markdown, title, onClose }: DocumentationSidebarProps) => {
  const { openLightbox } = useLightbox();

  // Split markdown into sections based on "## " (h2) headers
  const markdownSectionSeparator = /(?=\n## )/;
  const sections = markdown.split(markdownSectionSeparator).map((section, index) => {
    const trimmedSection = section.trim();
    const firstLineEnd = trimmedSection.indexOf("\n");

    const titleStartIndex = 2; // The length of "## "
    const titleEndIndex = firstLineEnd;
    const title = trimmedSection.substring(titleStartIndex, titleEndIndex).trim();

    const contentStartIndex = firstLineEnd + 1;
    const content = trimmedSection.substring(contentStartIndex).trim();
    return { title, content, index };
  });

  const MarkdownTaskListItem = ({ children }: { children?: React.ReactNode }) => {
    return (
      <li className="flex items-start pb-2 pl-0">
        <CheckCircleIcon className="mt-2 mr-2 size-4 shrink-0 text-emerald-600" />
        <span className="flex-1">{children}</span>
      </li>
    );
  };

  type MarkdownBlockProps = {
    node: Element | undefined;
    children?: React.ReactNode;
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const MarkdownCodeBlock = ({ node, ...props }: MarkdownBlockProps) => {
    // this is unfortunate; react-markdown returns nested children, in this case the code block string will always be nested 1 object deep
    const textToCopy = useMemo(() => {
      return React.Children.toArray(props.children)
        .map((child: any) => child?.props?.children || "")
        .join("");
    }, [props.children]);

    return (
      <div
        className="
          not-prose mt-1 inline-flex w-full resize-y overflow-auto rounded-md border border-gray-300 bg-gray-50 py-2
          pr-1 pl-3 font-mono text-sm text-gray-500 shadow-sm
        "
      >
        <pre className="mr-3 max-h-48 w-full whitespace-pre-wrap" {...props} />
        <CopyToClipboard textToCopy={textToCopy} className="sticky top-0 self-start" />
      </div>
    );
  };

  // Custom implementation of markdown callouts.
  const MarkdownCalloutBlockquote = ({ node, ...props }: MarkdownBlockProps) => {
    const getStyleFromFirstEmoji = useCallback((node) => {
      if (!node || !node.children) {
        return "";
      }

      for (const child of node.children) {
        const calloutStyle = "prose-p:p-0 prose-p:m-0 my-4 p-2 flex flex-col gap-2 rounded-sm";
        if (child.value) {
          if (child.value.startsWith("❗️")) {
            return `bg-red-100 border-l-4 border-red-500 ${calloutStyle}`;
          }
          if (child.value.startsWith("📘")) {
            return `bg-blue-100 border-l-4 border-blue-500 ${calloutStyle}`;
          }
          if (child.value.startsWith("👍")) {
            return `bg-green-100 border-l-4 border-green-500 ${calloutStyle}`;
          }
          if (child.value.startsWith("🚧")) {
            return `bg-yellow-100 border-l-4 border-yellow-500 ${calloutStyle}`;
          }
        }
        const nestedEmoji = getStyleFromFirstEmoji(child);
        if (nestedEmoji) {
          return nestedEmoji;
        }
      }

      return "bg-gray-100 border-l-4 border-gray-500";
    }, []);

    const blockquoteStyle = useMemo(() => getStyleFromFirstEmoji(node), [node, getStyleFromFirstEmoji]);

    return (
      <blockquote
        className={`
          font-normal text-slate-500 not-italic
          prose-p:before:content-none
          prose-p:after:content-none
          ${blockquoteStyle}
        `}
        {...props}
      />
    );
  };

  type MarkdownLightboxImageProps = {
    node: Element | undefined;
    src?: string;
    alt?: string;
  };
  const MarkdownLightboxImage = ({ src, alt }: MarkdownLightboxImageProps) => {
    return <img src={src} alt={alt} className="cursor-pointer" onClick={() => openLightbox(src || "", alt || "")} />;
  };

  const markdownFilename = title.toLowerCase().split(" ").join("-") + ".md";

  const downloadMarkdown = () => {
    const element = document.createElement("a");
    const file = new Blob([markdown], { type: "text/markdown" });
    element.href = URL.createObjectURL(file);
    element.download = markdownFilename;
    document.body.appendChild(element);
    element.click();
  };

  return (
    <div className="prose prose-sm flex size-full max-w-none flex-col space-y-1.5 bg-neutral-50 prose-slate">
      <div className="not-prose flex items-center justify-between border-b px-4 py-3">
        <div className="text-lg font-semibold text-gray-900 capitalize">{title}</div>
        <div className="flex items-center">
          <DownloadMenu menuItems={[{ text: "Download .md", onClick: downloadMarkdown }]} />
          <button
            onClick={onClose}
            className="
              ml-1 rounded-md p-1 text-gray-700
              hover:bg-slate-100
              focus:outline-none
            "
          >
            <XIcon className="size-5 stroke-gray-600 stroke-[1.5]" aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto px-2">
        {sections.map((section) => (
          <div key={section.index} className="mt-1">
            <SectionExpandable label={section.title} key={section.index} transparent={true} defaultOpen={true}>
              <div
                className="
                  prose-code:rounded-md prose-code:bg-gray-100 prose-code:p-1
                  prose-code:before:hidden
                  prose-code:after:hidden
                "
              >
                <ReactMarkdown
                  unwrapDisallowed={true}
                  // add support for github flavored markdown (tasklists and callouts)
                  remarkPlugins={[remarkGfm]}
                  disallowedElements={["input"]}
                  components={{
                    h1: ({ node, ...props }) => <p {...props} />, // eslint-disable-line @typescript-eslint/no-unused-vars
                    h2: ({ node, ...props }) => <p {...props} />, // eslint-disable-line @typescript-eslint/no-unused-vars
                    // h4 because h3 looks too big
                    h3: ({ node, ...props }) => <h4 {...props} />, // eslint-disable-line @typescript-eslint/no-unused-vars
                    blockquote: ({ node, ...props }) => <MarkdownCalloutBlockquote node={node} {...props} />,
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    li: ({ node, className, ...props }) => {
                      return className?.includes("task-list-item") ? (
                        <MarkdownTaskListItem {...props} />
                      ) : (
                        <li {...props} />
                      );
                    },
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    ul: ({ node, className, ...props }) => {
                      return className?.includes("task-list") ? <ul {...props} className="pl-0" /> : <ul {...props} />;
                    },
                    pre: ({ node, ...props }) => <MarkdownCodeBlock node={node} {...props} />,
                    img: ({ node, ...props }) => <MarkdownLightboxImage node={node} {...props} />,
                  }}
                >
                  {section.content}
                </ReactMarkdown>
              </div>
            </SectionExpandable>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentationSidebar;
